/**
 * ReportIssueTab — Bridge to the existing FeedbackCaptureOverlay
 *
 * Provides context about the issue reporting tool and launches the
 * full-screen capture overlay when the user clicks "Start Report".
 */

import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    alpha,
    useTheme,
} from '@mui/material';
import {
    CameraAlt as ScreenshotIcon,
    Draw as AnnotateIcon,
    BugReport as BugIcon,
    Send as SubmitIcon,
} from '@mui/icons-material';
import { useSupportHub } from '../SupportHubContext';

const STEPS = [
    { icon: <ScreenshotIcon fontSize="small" />, label: 'Capture a screenshot', description: 'We automatically snap the current page' },
    { icon: <AnnotateIcon fontSize="small" />, label: 'Annotate the issue', description: 'Draw, highlight, or arrow-point to the problem' },
    { icon: <SubmitIcon fontSize="small" />, label: 'Describe & submit', description: 'Add notes and send to our dev team' },
];

const ReportIssueTab: React.FC = () => {
    const theme = useTheme();
    const { openOverlay, closePanel } = useSupportHub();
    const [isStarting, setIsStarting] = useState(false);
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const handleStartReport = () => {
        if (isStarting) return;
        setIsStarting(true);
        closePanel();
        // Small delay to let the panel close animation complete before opening overlay.
        // Note: Do not clear this timeout on unmount because calling closePanel() above
        // intentionally unmounts this tab; clearing on unmount cancels the overlay launch.
        setTimeout(() => {
            openOverlay();
            if (isMountedRef.current) {
                setIsStarting(false);
            }
        }, 300);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pb: 0.5 }}>
            {/* ── Header ── */}
            <Box
                sx={{
                    textAlign: 'center',
                    p: 1.5,
                    borderRadius: '12px',
                    background: `linear-gradient(135deg, ${alpha('#eb3349', 0.08)} 0%, ${alpha('#f45c43', 0.05)} 100%)`,
                    border: `1px solid ${alpha('#eb3349', 0.15)}`,
                }}
            >
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 0.75,
                    }}
                >
                    <BugIcon sx={{ color: '#fff', fontSize: '1.1rem' }} />
                </Box>
                <Typography variant="body2" fontWeight={600}>
                    Report an Issue
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    Found a bug? Help us fix it with a visual report
                </Typography>
            </Box>

            {/* ── How it works ── */}
            <Box>
                <Typography variant="caption" fontWeight={600} color="text.secondary" textTransform="uppercase" letterSpacing={0.5}>
                    How it works
                </Typography>
                <List dense disablePadding sx={{ mt: 0.5 }}>
                    {STEPS.map((step, i) => (
                        <ListItem key={i} disableGutters sx={{ py: 0.5 }}>
                            <ListItemIcon
                                sx={{
                                    minWidth: 32,
                                    color: theme.palette.secondary.main,
                                }}
                            >
                                {step.icon}
                            </ListItemIcon>
                            <ListItemText
                                disableTypography
                                primary={
                                    <Typography component="span" display="block" variant="body2" fontSize="0.82rem" fontWeight={500}>
                                        {step.label}
                                    </Typography>
                                }
                                secondary={
                                    <Typography component="span" display="block" variant="caption" color="text.secondary">
                                        {step.description}
                                    </Typography>
                                }
                            />
                        </ListItem>
                    ))}
                </List>
            </Box>

            {/* ── CTA ── */}
            <Button
                variant="contained"
                fullWidth
                disabled={isStarting}
                onClick={handleStartReport}
                startIcon={<ScreenshotIcon />}
                aria-haspopup="dialog"
                aria-label="Start visual issue report"
                sx={{
                    minHeight: 44,
                    py: 1.25,
                    borderRadius: '10px',
                    fontWeight: 600,
                    mb: 1.5,
                    background: 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)',
                    '&:hover': {
                        background: 'linear-gradient(135deg, #d62839 0%, #e04b3b 100%)',
                    },
                }}
            >
                {isStarting ? 'Starting...' : 'Start Report'}
            </Button>
        </Box>
    );
};

export default ReportIssueTab;
